import * as React from "react";

import {
  Box,
  CircularProgress,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloudDoneIcon from "@material-ui/icons/CloudDone";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import * as Sentry from "@sentry/browser";
import { useMutation } from "react-query";

import { MopButton } from "client/components";
import { MopButtonVariant } from "client/components/MopButton/MopButton";
import { useBranding, useMerchantModel } from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { AwsService } from "client/services";

interface FileUploadProps {
  customHelperText?: React.ReactNode;
  errorMessage?: string;
  fileExtensionsToAccept?: string;
  filename?: string;
  hasError: boolean;
  helperText?: string;
  id: string;
  label?: string;
  propertyName: string;
  updateFieldValue: (value: string, filename?: string) => void;
  handleFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const CASH_S3_BUCKET_FIELD = "cashPayLogoUrl";

const useStyles = makeStyles((theme) => ({
  helperText: {
    fontSize: "0.9rem",
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  root: {
    "&.MuiButton-contained": {
      border: `1px solid ${theme.palette.common.black}`,
      background: theme.palette.common.white,
    },
  },
}));

const HelperText: React.FC<{
  helperText?: string;
  customHelperText?: React.ReactNode;
}> = ({ helperText, customHelperText }) => {
  const classes = useStyles();
  let helperTextNode = null;
  if (customHelperText) {
    helperTextNode = customHelperText;
  } else if (Boolean(helperText)) {
    helperTextNode = (
      <Typography
        className={classes.helperText}
        variant="body2"
        color="inherit"
      >
        {helperText}
      </Typography>
    );
  }
  return helperTextNode ? <Box mb={0.3}>{helperTextNode}</Box> : null;
};

const SelectedFilename: React.FC<{ name?: string }> = ({ name }) => {
  const classes = useStyles();
  if (!Boolean(name)) return null;
  return (
    <Box display="flex" mb={0.3}>
      <Typography>{name}</Typography>
      <Box ml={0.5} color="success">
        <CheckCircleIcon className={classes.successIcon} />
      </Box>
    </Box>
  );
};

export const ButtonIcon: React.FC<{
  isLoading: boolean;
  isSuccess: boolean;
}> = ({ isLoading, isSuccess }) => {
  if (isSuccess) return <CloudDoneIcon />;
  if (isLoading) return <CircularProgress size={20} color="secondary" />;
  return <CloudUploadIcon />;
};

export const FileUploadField: React.FC<FileUploadProps> = ({
  customHelperText,
  errorMessage,
  fileExtensionsToAccept = "image/png, image/jpeg, application/pdf, image/heic",
  filename,
  hasError,
  helperText,
  id,
  label,
  propertyName,
  updateFieldValue,
  handleFileChange = undefined,
}) => {
  const [selectedFile, setSelectedFile] = React.useState("");
  const [error, setError] = React.useState("");

  const { brandKey } = useBranding();
  const isCash = brandKey === "CashApp";

  const classes = useStyles();
  const { merchant } = useMerchantModel();
  const { mutate: uploadFile, isLoading, isSuccess } = useMutation(
    (params: AwsService.UploadFileToS3Params) => AwsService.uploadFile(params),
    {
      onSuccess: (url, { file }) => {
        setSelectedFile(file.name);
        updateFieldValue(url, file.name);
        logAmplitudeEvent("UPLOAD", `${propertyName}_upload_success`);
      },
      onError: (e: Error, { file }) => {
        setError(e.message);
        logAmplitudeEvent("UPLOAD", `${propertyName}_upload_error`);
        Sentry.captureException(
          new Error(
            `Error uploading ${propertyName}. Attempted to upload ${
              file.name
            } to S3 :: ${e.message}, merchantId: ${
              merchant.merchantAccount?.merchantId ?? "N/A"
            }, mlsProspectRef: ${
              merchant.merchantAccount?.mlsProspectRef ?? "N/A"
            }`
          )
        );
      },
    }
  );

  React.useEffect(() => {
    if (errorMessage) {
      setError(errorMessage);
    }
  }, [hasError, errorMessage]);

  function _handleFileChange({
    currentTarget,
  }: React.ChangeEvent<HTMLInputElement>) {
    const file = currentTarget?.files?.[0];

    // reset error message
    if (error) {
      setError("");
    }

    if (file) {
      // filename in props never include extension for ex. it can be gerard_degas_govId so we need to add it if it's passed in
      const _filename = Boolean(filename)
        ? `${filename}.${file.name.split(".").pop()}`
        : undefined;

      const bucketType =
        propertyName === CASH_S3_BUCKET_FIELD ? "Cash" : "Afterpay";

      uploadFile({ file, filename: _filename, bucketType });
    }
  }

  const truncateFilename = (filename: string) => {
    // so the filename doesn't overflow the length of the button
    const result =
      filename.length > 40
        ? `${filename.slice(0, 35)}...${filename.split(".").pop()}`
        : filename;
    return result;
  };

  let buttonVariant: MopButtonVariant = isCash ? "secondary" : "primary";
  if (propertyName === CASH_S3_BUCKET_FIELD) {
    buttonVariant = "tertiary";
  }

  return (
    <Box data-testid={propertyName}>
      <Box mb={1}>
        <Typography>{label}</Typography>
      </Box>
      <input
        accept={fileExtensionsToAccept}
        disabled={isLoading}
        hidden
        id={id}
        onChange={
          Boolean(handleFileChange) ? handleFileChange : _handleFileChange
        }
        type="file"
      />
      <Box mb={1}>
        <Tooltip title="Select File">
          <label aria-disabled={isLoading} htmlFor={id}>
            <MopButton
              mopVariant={buttonVariant}
              fullWidth
              component="span"
              startIcon={
                <ButtonIcon isSuccess={isSuccess} isLoading={isLoading} />
              }
              disabled={isLoading}
              onClick={() => {
                // field property name
                propertyName &&
                  logAmplitudeEvent("UPLOAD", `${propertyName}_upload_click`);
              }}
            >
              Upload
            </MopButton>
          </label>
        </Tooltip>
      </Box>
      <SelectedFilename name={truncateFilename(selectedFile)} />
      <HelperText helperText={helperText} customHelperText={customHelperText} />
      {Boolean(error) && (
        <Box mb={0.3}>
          <Typography
            className={classes.helperText}
            variant="body2"
            color="error"
          >
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
